<template>
  <div class="purchase-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>采购管理</span>
          <div>
            <el-button type="success" @click="handleExport" :loading="exportLoading">导出采购单</el-button>
            <el-button type="primary" @click="handleCreate">新增采购单</el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :inline="true" :model="queryParams" class="search-form">
        <el-form-item label="关键词">
          <el-input
            v-model="queryParams.keyword"
            placeholder="采购单号/供应商名称"
            clearable
            @keyup.enter="handleSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="采购状态">
          <el-select v-model="queryParams.purchaseStatus" placeholder="全部" clearable style="width: 150px">
            <el-option label="待采购" value="PENDING" />
            <el-option label="采购中" value="PROCESSING" />
            <el-option label="已完成" value="COMPLETED" />
            <el-option label="已取消" value="CANCELLED" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 采购单列表 -->
      <el-table
        v-loading="loading"
        :data="purchaseList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="purchaseOrderNumber" label="采购单号" width="150" />
        <el-table-column prop="supplierName" label="供应商" width="150" />
        <el-table-column prop="supplierContactPerson" label="供应商联系人" width="120" />
        <el-table-column prop="supplierContactPhone" label="联系电话" width="120" />
        <el-table-column prop="warehouseName" label="仓库" width="120" />
        <el-table-column prop="purchaseStatus" label="采购状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.purchaseStatus)">
              {{ getStatusName(row.purchaseStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="purchaseDate" label="采购日期" width="120">
          <template #default="{ row }">
            {{ formatDate(row.purchaseDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="expectedDeliveryDate" label="预计到货日期" width="120">
          <template #default="{ row }">
            {{ formatDate(row.expectedDeliveryDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="actualDeliveryDate" label="实际到货日期" width="120">
          <template #default="{ row }">
            {{ formatDate(row.actualDeliveryDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="totalAmount" label="金额" width="100">
          <template #default="{ row }">
            {{ row.totalAmount ? '¥' + row.totalAmount : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" />
        <el-table-column label="操作" width="280" fixed="right">
          <template #default="{ row }">
            <el-button type="info" size="small" @click="handleViewDetail(row)" style="margin-right: 10px">详情</el-button>
            <el-button 
              type="primary" 
              size="small" 
              @click="handleEdit(row)"
              style="margin-right: 10px"
            >
              编辑
            </el-button>
            <el-tooltip
              :content="!canDelete(row.purchaseStatus) ? getDeleteTooltip(row.purchaseStatus) : ''"
              placement="top"
              :disabled="canDelete(row.purchaseStatus)"
            >
              <span>
                <el-button 
                  type="danger" 
                  size="small" 
                  :disabled="!canDelete(row.purchaseStatus)"
                  @click="handleDelete(row)"
                >
                  删除
                </el-button>
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 创建/编辑采购单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="1000px"
      @close="handleDialogClose"
    >
      <el-form
        ref="purchaseFormRef"
        :model="purchaseForm"
        :rules="purchaseFormRules"
        label-width="120px"
      >
        <el-form-item label="采购单号" prop="purchaseOrderNumber">
          <el-input v-model="purchaseForm.purchaseOrderNumber" />
        </el-form-item>
        <el-form-item label="供应商" prop="supplierName">
          <el-input v-model="purchaseForm.supplierName" />
        </el-form-item>
        <el-form-item label="仓库" prop="warehouseId">
          <el-select
            v-model="purchaseForm.warehouseId"
            placeholder="请选择仓库"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="warehouse in warehouseList"
              :key="warehouse.id"
              :label="warehouse.warehouseName"
              :value="warehouse.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="采购状态" prop="purchaseStatus">
          <el-select v-model="purchaseForm.purchaseStatus" style="width: 100%">
            <el-option label="待采购" value="PENDING" />
            <el-option label="采购中" value="PROCESSING" />
            <el-option label="已完成" value="COMPLETED" />
            <el-option label="已取消" value="CANCELLED" />
          </el-select>
        </el-form-item>
        <el-form-item label="商品明细" prop="items">
          <div style="width: 100%">
            <el-button type="primary" size="small" @click="handleAddProduct">添加商品</el-button>
            <el-table :data="purchaseForm.items" border style="width: 100%; margin-top: 10px" max-height="300">
              <el-table-column prop="productName" label="商品名称" width="150" />
              <el-table-column prop="spec" label="规格" width="120" />
              <el-table-column prop="unit" label="单位" width="80" />
              <el-table-column label="数量" width="120">
                <template #default="{ row, $index }">
                  <el-input-number
                    v-model="row.quantity"
                    :min="1"
                    :precision="0"
                    size="small"
                    style="width: 100%"
                    @change="handleQuantityChange($index)"
                  />
                </template>
              </el-table-column>
              <el-table-column label="采购价" width="140">
                <template #default="{ row, $index }">
                  <el-input-number
                    v-model="row.unitPrice"
                    :min="0"
                    :precision="2"
                    size="small"
                    style="width: 100%"
                    @change="handleUnitPriceChange($index)"
                  />
                </template>
              </el-table-column>
              <el-table-column label="小计" width="120">
                <template #default="{ row }">
                  {{ row.totalPrice ? '¥' + row.totalPrice.toFixed(2) : '-' }}
                </template>
              </el-table-column>
              <el-table-column label="操作" width="80" fixed="right">
                <template #default="{ $index }">
                  <el-button type="danger" size="small" @click="handleRemoveProduct($index)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
            <div style="text-align: right; margin-top: 10px; font-weight: bold">
              合计金额：{{ calculateTotalAmount() }}
            </div>
          </div>
        </el-form-item>
        <el-form-item label="供应商联系人" prop="supplierContactPerson">
          <el-input v-model="purchaseForm.supplierContactPerson" placeholder="请输入供应商联系人" />
        </el-form-item>
        <el-form-item label="供应商联系电话" prop="supplierContactPhone">
          <el-input v-model="purchaseForm.supplierContactPhone" placeholder="请输入供应商联系电话" />
        </el-form-item>
        <el-form-item label="采购日期" prop="purchaseDate">
          <el-date-picker
            v-model="purchaseForm.purchaseDate"
            type="date"
            placeholder="选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="预计到货日期" prop="expectedDeliveryDate">
          <el-date-picker
            v-model="purchaseForm.expectedDeliveryDate"
            type="date"
            placeholder="选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="实际到货日期" prop="actualDeliveryDate">
          <el-date-picker
            v-model="purchaseForm.actualDeliveryDate"
            type="date"
            placeholder="选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="purchaseForm.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>

    <!-- 商品选择对话框 -->
    <el-dialog
      v-model="productPickerVisible"
      title="选择商品"
      width="900px"
    >
      <div style="margin-bottom: 10px; color: #909399; font-size: 12px">
        {{ purchaseForm.warehouseName ? `当前仓库「${purchaseForm.warehouseName}」的商品列表` : '当前企业所有商品列表' }}
      </div>
      <!-- 商品查询表单 -->
      <el-form :inline="true" :model="productQueryParams" class="search-form" style="margin-bottom: 15px">
        <el-form-item label="关键词">
          <el-input
            v-model="productQueryParams.keyword"
            placeholder="商品名称/编码/规格"
            clearable
            @keyup.enter="handleProductSearch"
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleProductSearch">查询</el-button>
          <el-button @click="handleProductReset">重置</el-button>
        </el-form-item>
      </el-form>
      <el-table
        :data="productList"
        border
        style="width: 100%"
        v-loading="productLoading"
      >
        <el-table-column prop="productCode" label="商品编码" width="120" />
        <el-table-column prop="productName" label="商品名称" width="150" />
        <el-table-column prop="spec" label="规格" width="120" />
        <el-table-column prop="unit" label="单位" width="80" />
        <el-table-column prop="purchasePrice" label="采购价" width="100">
          <template #default="{ row }">
            {{ row.purchasePrice ? '¥' + row.purchasePrice : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button 
              type="primary" 
              size="small" 
              @click="selectProduct(row)"
            >
              选择
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <!-- 分页 -->
      <div class="pagination" style="margin-top: 15px">
        <el-pagination
          v-model:current-page="productQueryParams.page"
          v-model:page-size="productQueryParams.pageSize"
          :total="productTotal"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleProductSizeChange"
          @current-change="handleProductPageChange"
        />
      </div>
    </el-dialog>

    <!-- 采购单详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="采购单详情"
      width="1000px"
      class="purchase-detail-dialog"
    >
      <div v-if="detailData" class="purchase-detail-content">
        <!-- 基本信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><Document /></el-icon>
              <span>基本信息</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="采购单号" label-class-name="detail-label">
              <span class="detail-value highlight">{{ detailData.purchaseOrderNumber }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="供应商" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.supplierName || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="仓库" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.warehouseName || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="采购状态" label-class-name="detail-label">
              <el-tag :type="getStatusType(detailData.purchaseStatus)" size="large">
                {{ getStatusName(detailData.purchaseStatus) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="采购日期" label-class-name="detail-label">
              <span class="detail-value">{{ formatDate(detailData.purchaseDate) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="预计到货日期" label-class-name="detail-label">
              <span class="detail-value">{{ formatDate(detailData.expectedDeliveryDate) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="实际到货日期" label-class-name="detail-label">
              <span class="detail-value">{{ formatDate(detailData.actualDeliveryDate) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="金额" label-class-name="detail-label">
              <span class="detail-value amount">{{ detailData.totalAmount ? '¥' + detailData.totalAmount.toFixed(2) : '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 供应商信息卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><User /></el-icon>
              <span>供应商信息</span>
            </div>
          </template>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="供应商联系人" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.supplierContactPerson || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="供应商联系电话" label-class-name="detail-label">
              <span class="detail-value">{{ detailData.supplierContactPhone || '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 商品明细卡片 -->
        <el-card class="detail-card" shadow="never">
          <template #header>
            <div class="card-title">
              <el-icon><Goods /></el-icon>
              <span>商品明细</span>
            </div>
          </template>
          <el-table 
            :data="detailData.items" 
            border 
            style="width: 100%" 
            v-if="detailData.items && detailData.items.length > 0"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
            :row-style="{ height: '50px' }"
          >
            <el-table-column prop="productName" label="商品名称" width="150" />
            <el-table-column prop="spec" label="规格" width="120" />
            <el-table-column prop="unit" label="单位" width="80" />
            <el-table-column prop="quantity" label="数量" width="100" align="right" />
            <el-table-column prop="unitPrice" label="采购价" width="120" align="right">
              <template #default="{ row }">
                {{ row.unitPrice ? '¥' + row.unitPrice.toFixed(2) : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="totalPrice" label="小计" width="120" align="right">
              <template #default="{ row }">
                {{ row.totalPrice ? '¥' + row.totalPrice.toFixed(2) : '-' }}
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-else description="暂无商品明细" :image-size="80" />
          <div v-if="detailData.items && detailData.items.length > 0" style="text-align: right; margin-top: 15px; font-weight: bold; font-size: 16px">
            合计金额：{{ detailData.totalAmount ? '¥' + detailData.totalAmount.toFixed(2) : '-' }}
          </div>
        </el-card>

        <!-- 备注信息卡片 -->
        <el-card class="detail-card" shadow="never" v-if="detailData.remark">
          <template #header>
            <div class="card-title">
              <el-icon><EditPen /></el-icon>
              <span>备注</span>
            </div>
          </template>
          <div class="remark-content">{{ detailData.remark }}</div>
        </el-card>
      </div>
      <el-empty v-else description="未找到详情" :image-size="80" />
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Document, User, Goods, EditPen } from '@element-plus/icons-vue'
import {
  getPurchaseOrderList,
  getPurchaseOrderDetail,
  createPurchaseOrder,
  updatePurchaseOrder,
  deletePurchaseOrder,
  exportPurchaseOrderList
} from '../api/internalPurchase'
import { getWarehouseList } from '../api/internalWarehouse'
import { getProductList, getProductDetail, updateProduct } from '../api/internalProduct'

// 数据
const loading = ref(false)
const purchaseList = ref([])
const total = ref(0)
const exportLoading = ref(false)
// 上次导出时间，用于限流
const lastExportTime = ref(0)
// 导出限流间隔时间（毫秒），3秒内不允许重复导出
const EXPORT_THROTTLE_INTERVAL = 3000
const warehouseList = ref([])
const queryParams = reactive({
  keyword: '',
  purchaseStatus: '',
  page: 1,
  pageSize: 10
})

// 对话框
const dialogVisible = ref(false)
const dialogTitle = ref('新增采购单')
const isEdit = ref(false)
const purchaseFormRef = ref(null)

// 详情对话框
const detailDialogVisible = ref(false)
const detailData = ref(null)
const purchaseForm = reactive({
  id: null,
  purchaseOrderNumber: '',
  supplierId: null,
  supplierName: '',
  warehouseId: null,
  warehouseName: '',
  purchaseStatus: 'PENDING',
  purchaseDate: null,
  expectedDeliveryDate: null,
  actualDeliveryDate: null,
  supplierContactPerson: '',
  supplierContactPhone: '',
  totalAmount: null,
  remark: '',
  items: []
})

// 商品选择相关
const productPickerVisible = ref(false)
const productList = ref([])
const productTotal = ref(0)
const productLoading = ref(false)
const productQueryParams = reactive({
  keyword: '',
  page: 1,
  pageSize: 10
})

// 验证手机号或固定电话格式
const validatePhone = (rule, value, callback) => {
  if (!value) {
    callback()
    return
  }
  // 手机号格式：11位数字，以1开头
  const mobilePattern = /^1[3-9]\d{9}$/
  // 固定电话格式：区号（3-4位）- 号码（7-8位），或直接7-8位号码
  const landlinePattern = /^(\d{3,4}-)?\d{7,8}$/
  
  if (mobilePattern.test(value) || landlinePattern.test(value)) {
    callback()
  } else {
    callback(new Error('请输入正确的手机号或固定电话格式'))
  }
}

const purchaseFormRules = {
  purchaseOrderNumber: [{ required: true, message: '请输入采购单号', trigger: 'blur' }],
  supplierName: [{ required: true, message: '请输入供应商名称', trigger: 'blur' }],
  supplierContactPerson: [{ required: true, message: '请输入供应商联系人', trigger: 'blur' }],
  supplierContactPhone: [
    { required: true, message: '请输入供应商联系电话', trigger: 'blur' },
    { validator: validatePhone, trigger: 'blur' }
  ]
}

// 加载仓库列表
const loadWarehouseList = async () => {
  try {
    const res = await getWarehouseList({ page: 1, pageSize: 1000 })
    if (res.code === '000000') {
      warehouseList.value = res.data.list || []
    }
  } catch (error) {
    console.error('获取仓库列表失败:', error)
  }
}

// 方法
const getStatusName = (status) => {
  const statusMap = {
    'PENDING': '待采购',
    'PROCESSING': '采购中',
    'COMPLETED': '已完成',
    'CANCELLED': '已取消'
  }
  return statusMap[status] || status
}

const getStatusType = (status) => {
  const typeMap = {
    'PENDING': 'warning',
    'PROCESSING': 'info',
    'COMPLETED': 'success',
    'CANCELLED': 'danger'
  }
  return typeMap[status] || ''
}

// 判断是否可以删除
const canDelete = (status) => {
  // 采购中和已完成状态不允许删除，其他状态可以删除
  return status !== 'PROCESSING' && status !== 'COMPLETED'
}

// 获取删除按钮的提示信息
const getDeleteTooltip = (status) => {
  if (status === 'PROCESSING') {
    return '采购中状态的采购单不允许删除'
  } else if (status === 'COMPLETED') {
    return '已完成状态的采购单不允许删除'
  }
  return ''
}

const formatDate = (date) => {
  if (!date) return '-'
  const d = new Date(date)
  return d.toLocaleDateString('zh-CN')
}

const loadPurchaseList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams }
    const res = await getPurchaseOrderList(params)
    if (res.code === '000000') {
      purchaseList.value = res.data.list || []
      total.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取采购单列表失败:', error)
    ElMessage.error('获取采购单列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  queryParams.page = 1
  loadPurchaseList()
}

const handleReset = () => {
  queryParams.keyword = ''
  queryParams.purchaseStatus = ''
  queryParams.page = 1
  loadPurchaseList()
}

const handleCreate = () => {
  dialogTitle.value = '新增采购单'
  isEdit.value = false
  Object.assign(purchaseForm, {
    id: null,
    purchaseOrderNumber: '',
    supplierId: null,
    supplierName: '',
    warehouseId: null,
    warehouseName: '',
    purchaseStatus: 'PENDING',
    purchaseDate: null,
    expectedDeliveryDate: null,
    actualDeliveryDate: null,
    supplierContactPerson: '',
    supplierContactPhone: '',
    totalAmount: null,
    remark: '',
    items: []
  })
  dialogVisible.value = true
}

const handleViewDetail = async (row) => {
  try {
    const res = await getPurchaseOrderDetail(row.id)
    if (res.code === '000000') {
      detailData.value = res.data
      detailDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取采购单详情失败:', error)
    ElMessage.error('获取采购单详情失败')
  }
}

const handleEdit = async (row) => {
  dialogTitle.value = '编辑采购单'
  isEdit.value = true
  try {
    const res = await getPurchaseOrderDetail(row.id)
    if (res.code === '000000') {
      Object.assign(purchaseForm, res.data)
      // 如果只有warehouseName而没有warehouseId，根据warehouseName查找warehouseId
      if (!purchaseForm.warehouseId && purchaseForm.warehouseName && warehouseList.value.length > 0) {
        const warehouse = warehouseList.value.find(w => w.warehouseName === purchaseForm.warehouseName)
        if (warehouse) {
          purchaseForm.warehouseId = warehouse.id
        }
      }
      // 确保 items 存在
      if (!purchaseForm.items) {
        purchaseForm.items = []
      } else {
        // 为每个商品明细保存原始采购价，用于后续判断是否修改
        // 先使用当前的unitPrice作为原始采购价，如果有productId则异步获取商品的实际采购价
        purchaseForm.items.forEach((item) => {
          // 先使用当前的unitPrice作为原始采购价（如果存在）
          if (!item.originalPurchasePrice) {
            item.originalPurchasePrice = item.unitPrice || 0
          }
        })
        
        // 异步获取商品的真实采购价（不阻塞界面）
        Promise.all(
          purchaseForm.items
            .filter(item => item.productId)
            .map(async (item) => {
              try {
                const productRes = await getProductDetail(item.productId)
                if (productRes.code === '000000' && productRes.data && productRes.data.purchasePrice !== undefined) {
                  // 如果商品有采购价，使用商品的采购价作为原始值
                  item.originalPurchasePrice = productRes.data.purchasePrice || 0
                }
              } catch (error) {
                console.error(`获取商品 ${item.productId} 详情失败:`, error)
                // 保持使用当前的unitPrice作为originalPurchasePrice
              }
            })
        ).catch(error => {
          console.error('批量获取商品详情失败:', error)
        })
      }
      // 处理日期格式
      if (purchaseForm.purchaseDate && purchaseForm.purchaseDate.length > 10) {
        purchaseForm.purchaseDate = purchaseForm.purchaseDate.substring(0, 10)
      }
      if (purchaseForm.expectedDeliveryDate && purchaseForm.expectedDeliveryDate.length > 10) {
        purchaseForm.expectedDeliveryDate = purchaseForm.expectedDeliveryDate.substring(0, 10)
      }
      if (purchaseForm.actualDeliveryDate && purchaseForm.actualDeliveryDate.length > 10) {
        purchaseForm.actualDeliveryDate = purchaseForm.actualDeliveryDate.substring(0, 10)
      }
      dialogVisible.value = true
    }
  } catch (error) {
    console.error('获取采购单详情失败:', error)
    ElMessage.error('获取采购单详情失败')
  }
}

const handleSubmit = async () => {
  if (!purchaseFormRef.value) return
  await purchaseFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        // 处理日期格式：如果只有日期（YYYY-MM-DD），转换为日期时间格式（YYYY-MM-DD HH:mm:ss）
        const submitData = { ...purchaseForm }
        if (submitData.purchaseDate && submitData.purchaseDate.length === 10) {
          submitData.purchaseDate = submitData.purchaseDate + ' 00:00:00'
        }
        if (submitData.expectedDeliveryDate && submitData.expectedDeliveryDate.length === 10) {
          submitData.expectedDeliveryDate = submitData.expectedDeliveryDate + ' 00:00:00'
        }
        if (submitData.actualDeliveryDate && submitData.actualDeliveryDate.length === 10) {
          submitData.actualDeliveryDate = submitData.actualDeliveryDate + ' 00:00:00'
        }
        // 自动计算总金额
        submitData.totalAmount = calculateTotalAmount()
        
        const res = isEdit.value 
          ? await updatePurchaseOrder(submitData) 
          : await createPurchaseOrder(submitData)
        if (res.code === '000000') {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          loadPurchaseList()
          
          // 异步更新商品的采购价（如果被修改了）
          updateProductPurchasePrices()
        }
      } catch (error) {
        console.error('保存采购单失败:', error)
        const errorData = error.response?.data
        ElMessage.error(errorData?.msg || errorData?.message || '保存采购单失败')
      }
    }
  })
}

const handleDialogClose = () => {
  purchaseFormRef.value?.resetFields()
}

// 商品选择相关方法
const loadProductList = async () => {
  productLoading.value = true
  try {
    const params = {
      page: productQueryParams.page,
      pageSize: productQueryParams.pageSize
    }
    // 如果有查询关键词，添加关键词参数
    if (productQueryParams.keyword) {
      params.keyword = productQueryParams.keyword
    }
    // 根据选择的仓库过滤商品
    if (purchaseForm.warehouseId) {
      params.warehouseId = purchaseForm.warehouseId
    }
    const res = await getProductList(params)
    if (res.code === '000000') {
      productList.value = res.data.list || []
      productTotal.value = res.data.total || 0
    }
  } catch (error) {
    console.error('获取商品列表失败:', error)
    ElMessage.error('获取商品列表失败')
  } finally {
    productLoading.value = false
  }
}

const handleAddProduct = () => {
  // 检查是否已选择仓库
  if (!purchaseForm.warehouseId) {
    ElMessage.warning('请先选择仓库')
    return
  }
  // 重置查询参数
  productQueryParams.keyword = ''
  productQueryParams.page = 1
  productQueryParams.pageSize = 10
  productPickerVisible.value = true
  loadProductList()
}

const handleProductSearch = () => {
  productQueryParams.page = 1
  loadProductList()
}

const handleProductReset = () => {
  productQueryParams.keyword = ''
  productQueryParams.page = 1
  loadProductList()
}

const handleProductSizeChange = (size) => {
  productQueryParams.pageSize = size
  productQueryParams.page = 1
  loadProductList()
}

const handleProductPageChange = (page) => {
  productQueryParams.page = page
  loadProductList()
}

const selectProduct = (product) => {
  // 检查是否已添加
  const exists = purchaseForm.items.find(item => item.productId === product.id)
  if (exists) {
    ElMessage.warning('该商品已添加，请修改数量')
    return
  }
  
  // 添加商品到明细，保存原始采购价用于后续比较
  const purchasePrice = product.purchasePrice || 0
  purchaseForm.items.push({
    productId: product.id,
    productCode: product.productCode,
    productName: product.productName,
    spec: product.spec || '',
    unit: product.unit || '',
    quantity: 1,
    unitPrice: purchasePrice,
    originalPurchasePrice: purchasePrice, // 保存原始采购价，用于判断是否修改
    totalPrice: purchasePrice,
    remark: ''
  })
  productPickerVisible.value = false
  calculateTotalAmount()
}

const handleQuantityChange = (index) => {
  calculateItemTotal(index)
}

const handleUnitPriceChange = (index) => {
  calculateItemTotal(index)
}

const calculateItemTotal = (index) => {
  const item = purchaseForm.items[index]
  if (item && item.quantity && item.unitPrice) {
    item.totalPrice = item.unitPrice * item.quantity
    calculateTotalAmount()
  }
}

const calculateTotalAmount = () => {
  let total = 0
  if (purchaseForm.items && purchaseForm.items.length > 0) {
    purchaseForm.items.forEach(item => {
      if (item.totalPrice) {
        total += item.totalPrice
      }
    })
  }
  purchaseForm.totalAmount = total
  return total.toFixed(2)
}

const handleRemoveProduct = (index) => {
  purchaseForm.items.splice(index, 1)
  calculateTotalAmount()
}

// 异步更新商品的采购价
const updateProductPurchasePrices = async () => {
  if (!purchaseForm.items || purchaseForm.items.length === 0) {
    return
  }
  
  // 找出采购价被修改的商品
  const productsToUpdate = []
  for (const item of purchaseForm.items) {
    if (item.productId && item.unitPrice !== undefined && item.unitPrice !== null) {
      const originalPrice = item.originalPurchasePrice || 0
      const currentPrice = item.unitPrice || 0
      
      // 如果采购价被修改了（允许小数点误差）
      if (Math.abs(currentPrice - originalPrice) > 0.01) {
        productsToUpdate.push({
          productId: item.productId,
          purchasePrice: currentPrice
        })
      }
    }
  }
  
  // 异步更新商品的采购价
  if (productsToUpdate.length > 0) {
    // 使用Promise.all并行更新，但不阻塞主流程
    Promise.all(
      productsToUpdate.map(async ({ productId, purchasePrice }) => {
        try {
          // 先获取商品详情
          const productRes = await getProductDetail(productId)
          if (productRes.code === '000000' && productRes.data) {
            const productData = { ...productRes.data }
            productData.purchasePrice = purchasePrice
            // 更新商品
            await updateProduct(productData)
            console.log(`商品 ${productId} 的采购价已更新为 ${purchasePrice}`)
          }
        } catch (error) {
          console.error(`更新商品 ${productId} 的采购价失败:`, error)
          // 不显示错误提示，避免影响用户体验
        }
      })
    ).catch(error => {
      console.error('批量更新商品采购价失败:', error)
    })
  }
}

const handleDelete = async (row) => {
  // 检查是否可以删除
  if (!canDelete(row.purchaseStatus)) {
    ElMessage.warning(getDeleteTooltip(row.purchaseStatus))
    return
  }
  
  try {
    await ElMessageBox.confirm('确定要删除该采购单吗？', '提示', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })
    const res = await deletePurchaseOrder(row.id)
    if (res.code === '000000') {
      ElMessage.success('删除成功')
      loadPurchaseList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除采购单失败:', error)
      ElMessage.error(error.response?.data?.message || '删除采购单失败')
    }
  }
}

const handleSizeChange = (size) => {
  queryParams.pageSize = size
  loadPurchaseList()
}

const handlePageChange = (page) => {
  queryParams.page = page
  loadPurchaseList()
}

// 导出采购单列表
const handleExport = async () => {
  // 限流检查：如果正在导出，直接返回
  if (exportLoading.value) {
    return
  }

  // 限流检查：如果距离上次导出时间太短，提示用户
  const now = Date.now()
  const timeSinceLastExport = now - lastExportTime.value
  if (lastExportTime.value > 0 && timeSinceLastExport < EXPORT_THROTTLE_INTERVAL) {
    const remainingSeconds = Math.ceil((EXPORT_THROTTLE_INTERVAL - timeSinceLastExport) / 1000)
    ElMessage.warning(`导出操作过于频繁，请等待 ${remainingSeconds} 秒后再试`)
    return
  }
  
  try {
    exportLoading.value = true
    
    const exportParams = {
      keyword: queryParams.keyword,
      purchaseStatus: queryParams.purchaseStatus
    }
    
    const blob = await exportPurchaseOrderList(exportParams)
    
    if (!blob || blob.size === 0) {
      throw new Error('导出失败：返回数据格式错误')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `采购单列表_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${Date.now()}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    ElMessage.success('导出成功')
    // 导出成功后才记录时间戳，失败时不限流
    lastExportTime.value = Date.now()
  } catch (error) {
    console.error('导出失败:', error)
    const errorMsg = error.message || '导出失败'
    // 如果是限流错误（数据量过大），使用warning提示
    if (errorMsg.includes('数据量过大') || errorMsg.includes('超过最大限制')) {
      ElMessage.warning(errorMsg)
    } else {
      ElMessage.error(errorMsg)
    }
    // 导出失败时不更新时间戳，允许用户立即重试
  } finally {
    exportLoading.value = false
  }
}

// 生命周期
onMounted(() => {
  loadWarehouseList()
  loadPurchaseList()
})
</script>

<style scoped>
.purchase-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 详情对话框样式 */
.purchase-detail-dialog .purchase-detail-content {
  max-height: 70vh;
  overflow-y: auto;
}

.purchase-detail-dialog .detail-card {
  margin-bottom: 20px;
}

.purchase-detail-dialog .detail-card:last-child {
  margin-bottom: 0;
}

.purchase-detail-dialog .card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
}

.purchase-detail-dialog .detail-label {
  background-color: #f5f7fa;
  font-weight: 600;
  width: 150px;
}

.purchase-detail-dialog .detail-value {
  color: #606266;
}

.purchase-detail-dialog .detail-value.highlight {
  color: #409eff;
  font-weight: 600;
}

.purchase-detail-dialog .detail-value.amount {
  color: #f56c6c;
  font-weight: 600;
  font-size: 16px;
}

.purchase-detail-dialog .remark-content {
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  color: #606266;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>

